import { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { fetchWithAuth } from "../api/fetchWithAuth";

function RequirementSystemLinkModal({ show, onClose, onSaved, requirement }) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [systemRequirements, setSystemRequirements] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const currentLinks = useMemo(() => requirement?.SystemRequirements ?? [], [requirement]);
  const hasMultipleLinks = currentLinks.length > 1;

  useEffect(() => {
    if (!show || !requirement) return;

    setSearch("");
    setDebouncedSearch("");
    setSelectedId(currentLinks.length === 1 ? String(currentLinks[0].id) : "");
  }, [show, requirement, currentLinks]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 180);

    return () => window.clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    if (!show || hasMultipleLinks) return;

    const controller = new AbortController();

    async function loadSystemRequirements() {
      setLoading(true);

      try {
        const params = new URLSearchParams();
        if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());

        const res = await fetchWithAuth(`/api/system-requirements?${params.toString()}`, {
          signal: controller.signal
        });

        if (!res.ok) {
          setSystemRequirements([]);
          return;
        }

        const data = await res.json();
        const byId = new Map();

        for (const item of currentLinks) byId.set(Number(item.id), item);
        for (const item of data) byId.set(Number(item.id), item);

        setSystemRequirements([...byId.values()]);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.warn("loadSystemRequirements aborted:", err.message);
        }
      } finally {
        setLoading(false);
      }
    }

    loadSystemRequirements();

    return () => controller.abort();
  }, [show, debouncedSearch, currentLinks, hasMultipleLinks]);

  const saveLink = async () => {
    if (!requirement || hasMultipleLinks) return;

    setSaving(true);
    try {
      const res = await fetchWithAuth(`/api/requirements/${requirement.id}/system-requirements`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          systemRequirementId: selectedId ? Number(selectedId) : null
        })
      });

      if (!res.ok) {
        const error = await res.json().catch(() => null);
        alert(error?.error || "Linking system requirement failed");
        return;
      }

      await onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!requirement) return null;

  return (
    <Modal show={show} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Link System Requirement</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="mb-3">
          <div className="fw-semibold">{requirement.number}</div>
          <div className="text-muted small">{requirement.name}</div>
        </div>

        {hasMultipleLinks ? (
          <div className="alert alert-warning mb-0">
            <div className="fw-semibold mb-2">Multiple system links found.</div>
            <div className="mb-2">Edit this relation from System Requirements.</div>
            <ul className="mb-0">
              {currentLinks.map((item) => (
                <li key={item.id}>
                  <span className="fw-semibold">{item.number}</span>
                  <span className="text-muted"> - {item.name}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <>
            <div className="d-flex align-items-center gap-2 mb-3">
              <Form.Control
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search system requirements..."
                style={{ maxWidth: "360px" }}
              />
              {loading && <Spinner animation="border" size="sm" />}
            </div>

            <div style={{ maxHeight: "54vh", overflowY: "auto" }}>
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "48px" }} />
                    <th style={{ width: "190px" }}>Number</th>
                    <th style={{ width: "120px" }}>Category</th>
                    <th>Name</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <Form.Check
                        type="radio"
                        name="systemRequirementId"
                        checked={selectedId === ""}
                        onChange={() => setSelectedId("")}
                        aria-label="No system requirement"
                      />
                    </td>
                    <td className="text-muted" colSpan={3}>No system requirement</td>
                  </tr>
                  {systemRequirements.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-muted py-4">
                        No system requirements found.
                      </td>
                    </tr>
                  ) : systemRequirements.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <Form.Check
                          type="radio"
                          name="systemRequirementId"
                          checked={selectedId === String(item.id)}
                          onChange={() => setSelectedId(String(item.id))}
                          aria-label={`Link ${item.number}`}
                        />
                      </td>
                      <td className="fw-semibold">{item.number}</td>
                      <td>{item.category}</td>
                      <td>{item.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="success" onClick={saveLink} disabled={saving || hasMultipleLinks}>
          {saving ? "Saving..." : "Save link"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default RequirementSystemLinkModal;
